import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { freightAPI, shippersAPI, consigneesAPI, carriersAPI, driversAPI, trucksAPI, inventoryItemsAPI } from '../../services/api';
import Table from '../Common/Table';
import Modal from '../Common/Modal';
import { packTruck } from '../../utils/packing/packTruck';

function parseNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function money2(n) {
  return Number(n || 0).toFixed(2);
}

function clamp01(x) {
  return Math.max(0, Math.min(1, x));
}

function weightColor(weight, minW, maxW) {
  const denom = maxW - minW;
  const t = denom > 0 ? clamp01((weight - minW) / denom) : 0;
  // Blue (light) -> Red (heavy)
  const r = Math.round(50 + 170 * t);
  const g = Math.round(100 + 80 * (1 - t));
  const b = Math.round(220 - 140 * t);
  return `rgb(${r},${g},${b})`;
}

function shadeRgb(rgb, factor) {
  // factor: -1..+1 (negative darker, positive lighter)
  const m = String(rgb || '').match(/^rgb\((\d+),(\d+),(\d+)\)$/);
  if (!m) return rgb;
  const r0 = Number(m[1]);
  const g0 = Number(m[2]);
  const b0 = Number(m[3]);
  const f = clamp01(Math.abs(factor));
  const mix = (c) => {
    if (factor >= 0) return Math.round(c + (255 - c) * f);
    return Math.round(c * (1 - f));
  };
  return `rgb(${mix(r0)},${mix(g0)},${mix(b0)})`;
}

function isoProject(x, y, z, scale, originX, originY) {
  // Isometric-ish projection.
  // x,y in feet on floor; z in feet upward.
  const sx = (x - y) * scale;
  const sy = (x + y) * scale * 0.5 - z * scale;
  return { x: originX + sx, y: originY + sy };
}

function poly(points) {
  return points.map((p) => `${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(' ');
}

function useMatchMedia(query) {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  });
  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);
  return matches;
}

const FreightManagement = () => {
  const isMobile = useMatchMedia('(max-width: 768px)');
  const [freight, setFreight] = useState([]);
  const [shippers, setShippers] = useState([]);
  const [consignees, setConsignees] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingFreight, setEditingFreight] = useState(null);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [selectedInventory, setSelectedInventory] = useState([]); // [{ inventory_items_id, quantity, ...itemFields }]
  const [inventoryQtyDraft, setInventoryQtyDraft] = useState({}); // id -> string
  const [activeLayerIndex, setActiveLayerIndex] = useState(1);
  const [loadViewMode, setLoadViewMode] = useState('3d'); // '3d' | 'layer'
  const [show3dLabels, setShow3dLabels] = useState(true);
  const [formData, setFormData] = useState({
    freight_number: '',
    shipper_id: '',
    consignee_id: '',
    carrier_id: '',
    driver_id: '',
    truck_id: '',
    pickup_date: '',
    delivery_date: '',
    status: 'pending',
    value: '',
    weight_tons: '',
    length_feet: '',
    width_feet: '',
    height_feet: '',
    quantity: '',
    description: '',
    special_instructions: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [freightData, shippersData, consigneesData, carriersData, driversData, trucksData] = await Promise.all([
        freightAPI.getAll(),
        shippersAPI.getAll(),
        consigneesAPI.getAll(),
        carriersAPI.getAll(),
        driversAPI.getAll(),
        trucksAPI.getAll(),
      ]);
      setFreight(freightData);
      setShippers(shippersData);
      setConsignees(consigneesData);
      setCarriers(carriersData);
      setDrivers(driversData);
      setTrucks(trucksData);
    } catch (error) {
      alert('Error loading data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const inventory_items = selectedInventory.map((si) => ({
        inventory_items_id: si.inventory_items_id,
        quantity: parseInt(si.quantity, 10) || 0,
      })).filter((x) => x.inventory_items_id && x.quantity > 0);

      const submitData = {
        ...formData,
        value: parseFloat(formData.value) || 0,
        weight_tons: parseFloat(formData.weight_tons) || 0,
        length_feet: parseFloat(formData.length_feet) || 0,
        width_feet: parseFloat(formData.width_feet) || 0,
        height_feet: parseFloat(formData.height_feet) || 0,
        quantity: parseInt(formData.quantity) || 0,
        shipper_id: parseInt(formData.shipper_id),
        consignee_id: parseInt(formData.consignee_id),
        carrier_id: formData.carrier_id ? parseInt(formData.carrier_id) : null,
        driver_id: formData.driver_id ? parseInt(formData.driver_id) : null,
        truck_id: formData.truck_id ? parseInt(formData.truck_id) : null,
        inventory_items,
      };
      if (editingFreight) {
        await freightAPI.update(editingFreight.id, submitData);
      } else {
        await freightAPI.create(submitData);
      }
      setShowModal(false);
      setEditingFreight(null);
      resetForm();
      loadData();
    } catch (error) {
      alert('Error saving freight: ' + error.message);
    }
  };

  const handleEdit = async (item) => {
    setEditingFreight(item);
    setFormData({
      freight_number: item.freight_number,
      shipper_id: item.shipper_id,
      consignee_id: item.consignee_id,
      carrier_id: item.carrier_id || '',
      driver_id: item.driver_id || '',
      truck_id: item.truck_id || '',
      pickup_date: item.pickup_date || '',
      delivery_date: item.delivery_date || '',
      status: item.status,
      value: item.value || '',
      weight_tons: item.weight_tons || '',
      length_feet: item.length_feet || '',
      width_feet: item.width_feet || '',
      height_feet: item.height_feet || '',
      quantity: item.quantity || '',
      description: item.description || '',
      special_instructions: item.special_instructions || '',
    });
    try {
      const inv = await freightAPI.getInventoryItems(item.id);
      setSelectedInventory(
        (inv || []).map((row) => ({
          inventory_items_id: row.inventory_items_id,
          quantity: String(row.quantity ?? ''),
          description: row.description,
          value: row.value,
          weight: row.weight,
          length_feet: row.length_feet,
          width_feet: row.width_feet,
          height_feet: row.height_feet,
          available_quantity: row.quantity, // informational (original selection)
        }))
      );
    } catch {
      setSelectedInventory([]);
    }
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this freight record?')) return;
    try {
      await freightAPI.delete(id);
      loadData();
    } catch (error) {
      alert('Error deleting freight: ' + error.message);
    }
  };

  const resetForm = () => {
    setFormData({
      freight_number: '',
      shipper_id: '',
      consignee_id: '',
      carrier_id: '',
      driver_id: '',
      truck_id: '',
      pickup_date: '',
      delivery_date: '',
      status: 'pending',
      value: '',
      weight_tons: '',
      length_feet: '',
      width_feet: '',
      height_feet: '',
      quantity: '',
      description: '',
      special_instructions: '',
    });
    setSelectedInventory([]);
    setInventoryItems([]);
    setInventoryQtyDraft({});
  };

  // Load inventory items for selected consignee (delivery side)
  useEffect(() => {
    const consigneeId = formData.consignee_id ? Number(formData.consignee_id) : null;
    if (!showModal || !consigneeId) {
      setInventoryItems([]);
      return;
    }
    let isStale = false;
    (async () => {
      try {
        const items = await inventoryItemsAPI.getAll({ consignee_id: String(consigneeId) });
        if (!isStale) setInventoryItems(Array.isArray(items) ? items : []);
      } catch {
        if (!isStale) setInventoryItems([]);
      }
    })();
    return () => { isStale = true; };
  }, [showModal, formData.consignee_id]);

  const selectedTruck = useMemo(() => {
    const id = formData.truck_id ? Number(formData.truck_id) : null;
    if (!id) return null;
    return (trucks || []).find((t) => Number(t.id) === id) || null;
  }, [formData.truck_id, trucks]);

  const computedTotals = useMemo(() => {
    const rows = selectedInventory || [];
    let totalQty = 0;
    let totalValue = 0;
    let totalWeightLbs = 0;

    for (const r of rows) {
      const qty = parseInt(r.quantity, 10) || 0;
      if (qty <= 0) continue;
      totalQty += qty;
      totalValue += parseNum(r.value) * qty;
      totalWeightLbs += parseNum(r.weight) * qty;
    }
    return {
      quantity: totalQty,
      value: totalValue,
      weight_lbs: totalWeightLbs,
      weight_tons: totalWeightLbs / 2000,
    };
  }, [selectedInventory]);

  const expandedPallets = useMemo(() => {
    const pallets = [];
    for (const r of selectedInventory || []) {
      const qty = parseInt(r.quantity, 10) || 0;
      if (qty <= 0) continue;
      for (let i = 0; i < qty; i += 1) {
        pallets.push({
          id: `${r.inventory_items_id}-${i + 1}`,
          l: parseNum(r.length_feet),
          w: parseNum(r.width_feet),
          h: parseNum(r.height_feet),
          weight_lbs: parseNum(r.weight),
          meta: {
            inventory_items_id: r.inventory_items_id,
            description: r.description,
          },
        });
      }
    }
    return pallets;
  }, [selectedInventory]);

  const packResult = useMemo(() => {
    if (!selectedTruck) return null;
    if (!expandedPallets.length) return null;
    return packTruck({
      truck: {
        length_feet: selectedTruck.length_feet,
        width_feet: selectedTruck.width_feet,
        height_feet: selectedTruck.height_feet,
        capacity_tons: selectedTruck.capacity_tons,
      },
      boxes: expandedPallets,
      options: { allowRotateLW: true },
    });
  }, [expandedPallets, selectedTruck]);

  // Keep freight totals in sync when inventory selection changes.
  useEffect(() => {
    if (!showModal) return;
    if (!selectedInventory.length) return;
    setFormData((prev) => {
      const next = {
        ...prev,
        quantity: String(computedTotals.quantity || ''),
        value: money2(computedTotals.value),
        weight_tons: String(Number(computedTotals.weight_tons || 0).toFixed(2)),
      };

      if (packResult?.used) {
        next.length_feet = String(Number(packResult.used.length_feet || 0).toFixed(2));
        next.width_feet = String(Number(packResult.used.width_feet || 0).toFixed(2));
        next.height_feet = String(Number(packResult.used.height_feet || 0).toFixed(2));
      } else {
        next.length_feet = '';
        next.width_feet = '';
        next.height_feet = '';
      }

      return next;
    });
  }, [computedTotals, packResult, selectedInventory.length, showModal]);

  useEffect(() => {
    if (!showModal) return;
    setActiveLayerIndex(1);
    setLoadViewMode(isMobile ? 'layer' : '3d');
    setShow3dLabels(!isMobile);
  }, [showModal, selectedTruck?.id, selectedInventory.length, isMobile]);

  const addInventoryItem = (item) => {
    const id = item.inventory_items_id;
    const available = parseInt(item.quantity, 10) || 0;
    const draftRaw = inventoryQtyDraft[id] ?? '';
    const draftQty = parseInt(draftRaw, 10);
    if (!draftQty || draftQty <= 0) {
      alert('Enter a quantity > 0 to add.');
      return;
    }
    const existing = selectedInventory.find((x) => String(x.inventory_items_id) === String(id));
    const existingQty = existing ? parseInt(existing.quantity, 10) || 0 : 0;
    if (draftQty + existingQty > available) {
      alert(`Quantity exceeds available inventory (available ${available}).`);
      return;
    }

    setSelectedInventory((prev) => {
      const next = [...prev];
      const idx = next.findIndex((x) => String(x.inventory_items_id) === String(id));
      const row = {
        inventory_items_id: id,
        description: item.description,
        value: item.value,
        weight: item.weight,
        length_feet: item.length_feet,
        width_feet: item.width_feet,
        height_feet: item.height_feet,
      };
      if (idx >= 0) {
        next[idx] = { ...next[idx], ...row, quantity: String(existingQty + draftQty) };
      } else {
        next.push({ ...row, quantity: String(draftQty) });
      }
      return next;
    });
    setInventoryQtyDraft((prev) => ({ ...prev, [id]: '' }));
  };

  const removeSelectedInventory = (inventoryItemId) => {
    setSelectedInventory((prev) => prev.filter((x) => String(x.inventory_items_id) !== String(inventoryItemId)));
  };

  const columns = [
    { key: 'freight_number', label: 'Freight #' },
    { key: 'shipper_name', label: 'Shipper' },
    { key: 'consignee_name', label: 'Consignee' },
    { key: 'status', label: 'Status' },
    { key: 'value', label: 'Value', format: (val) => `$${parseFloat(val || 0).toFixed(2)}` },
    { key: 'weight_tons', label: 'Weight (tons)' },
    { key: 'pickup_date', label: 'Pickup Date' },
    { key: 'delivery_date', label: 'Delivery Date' },
  ];

  return (
    <div className="management-page">
      <div className="page-header">
        <h1>Freight Management</h1>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <Link to="/freight/import" className="btn-secondary">Import PDF</Link>
          <button className="btn-primary" onClick={() => { setEditingFreight(null); resetForm(); setShowModal(true); }}>
            Add New Freight
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading...</div>
      ) : (
        <Table
          data={freight}
          columns={columns}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      )}

      <Modal
        show={showModal}
        onClose={() => { setShowModal(false); setEditingFreight(null); resetForm(); }}
        title={editingFreight ? 'Edit Freight' : 'Add New Freight'}
        size="xl"
      >
        <form onSubmit={handleSubmit} className="freight-form">
          <div className="form-row">
            <div className="form-group">
              <label>Freight Number *</label>
              <input
                type="text"
                value={formData.freight_number}
                onChange={(e) => setFormData({ ...formData, freight_number: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Status *</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                required
              >
                <option value="pending">Pending</option>
                <option value="in_transit">In Transit</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Shipper *</label>
              <select
                value={formData.shipper_id}
                onChange={(e) => setFormData({ ...formData, shipper_id: e.target.value })}
                required
              >
                <option value="">Select Shipper</option>
                {shippers.map(s => (
                  <option key={s.id} value={s.id}>{s.company_name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Consignee *</label>
              <select
                value={formData.consignee_id}
                onChange={(e) => {
                  setFormData({ ...formData, consignee_id: e.target.value });
                  setSelectedInventory([]);
                  setInventoryQtyDraft({});
                }}
                required
              >
                <option value="">Select Consignee</option>
                {consignees.map(c => (
                  <option key={c.id} value={c.id}>{c.company_name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group" style={{ marginTop: 12 }}>
            <label>Build freight from available inventory</label>
            {!formData.consignee_id ? (
              <div style={{ color: '#666', fontSize: 13 }}>Select a consignee to view available inventory items.</div>
            ) : (
              <>
                <div style={{ display: 'grid', gap: 8 }}>
                  {inventoryItems.length === 0 ? (
                    <div style={{ color: '#666', fontSize: 13 }}>No inventory items found for this consignee.</div>
                  ) : (
                    <div className="freight-inventory-scroll">
                      <table className="freight-inventory-table">
                        <thead>
                          <tr style={{ textAlign: 'left' }}>
                            <th style={{ padding: '6px 8px' }}>Item</th>
                            <th style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>Avail</th>
                            <th style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>Value</th>
                            <th style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>Weight (lb)</th>
                            <th style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>L/W/H (ft)</th>
                            <th style={{ padding: '6px 8px' }}>Add qty</th>
                            <th style={{ padding: '6px 8px' }} />
                          </tr>
                        </thead>
                        <tbody>
                          {inventoryItems.map((it) => (
                            <tr key={it.inventory_items_id} style={{ borderTop: '1px solid #eee' }}>
                              <td style={{ padding: '6px 8px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                <strong>#{it.inventory_items_id}</strong> {it.description || '—'}
                              </td>
                              <td style={{ padding: '6px 8px' }}>{it.quantity ?? '—'}</td>
                              <td style={{ padding: '6px 8px' }}>${money2(it.value)}</td>
                              <td style={{ padding: '6px 8px' }}>{Number(it.weight || 0).toFixed(2)}</td>
                              <td style={{ padding: '6px 8px' }}>
                                {Number(it.length_feet || 0).toFixed(2)}/{Number(it.width_feet || 0).toFixed(2)}/{Number(it.height_feet || 0).toFixed(2)}
                              </td>
                              <td style={{ padding: '6px 8px', width: 90 }}>
                                <input
                                  type="number"
                                  min="0"
                                  step="1"
                                  value={inventoryQtyDraft[it.inventory_items_id] ?? ''}
                                  onChange={(e) => setInventoryQtyDraft((prev) => ({ ...prev, [it.inventory_items_id]: e.target.value }))}
                                />
                              </td>
                              <td style={{ padding: '6px 8px' }}>
                                <button type="button" className="btn-secondary" onClick={() => addInventoryItem(it)}>
                                  Add
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {selectedInventory.length > 0 ? (
                  <div style={{ marginTop: 10, padding: 10, border: '1px solid #eee', borderRadius: 8 }}>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>Selected inventory</div>
                    <div className="freight-inventory-scroll">
                      <table className="freight-inventory-table freight-inventory-table--compact">
                        <thead>
                          <tr style={{ textAlign: 'left' }}>
                            <th style={{ padding: '6px 8px' }}>Item</th>
                            <th style={{ padding: '6px 8px' }}>Qty</th>
                            <th style={{ padding: '6px 8px' }} />
                          </tr>
                        </thead>
                        <tbody>
                          {selectedInventory.map((si) => (
                            <tr key={si.inventory_items_id} style={{ borderTop: '1px solid #eee' }}>
                              <td style={{ padding: '6px 8px' }}>
                                <strong>#{si.inventory_items_id}</strong> {si.description || '—'}
                              </td>
                              <td style={{ padding: '6px 8px', width: 90 }}>
                                <input
                                  type="number"
                                  min="0"
                                  step="1"
                                  value={si.quantity}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    setSelectedInventory((prev) =>
                                      prev.map((x) => (String(x.inventory_items_id) === String(si.inventory_items_id) ? { ...x, quantity: v } : x))
                                    );
                                  }}
                                />
                              </td>
                              <td style={{ padding: '6px 8px' }}>
                                <button type="button" className="btn-secondary" onClick={() => removeSelectedInventory(si.inventory_items_id)}>
                                  Remove
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="freight-totals-line">
                      Totals auto-updated: ${money2(computedTotals.value)} · Qty {computedTotals.quantity} · Weight {Number(computedTotals.weight_tons || 0).toFixed(2)} · L/W/H {Number(packResult?.used?.length_feet || 0).toFixed(2)}/{Number(packResult?.used?.width_feet || 0).toFixed(2)}/{Number(packResult?.used?.height_feet || 0).toFixed(2)}
                    </div>
                  </div>
                ) : null}
              </>
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Carrier</label>
              <select
                value={formData.carrier_id}
                onChange={(e) => setFormData({ ...formData, carrier_id: e.target.value })}
              >
                <option value="">Select Carrier</option>
                {carriers.map(c => (
                  <option key={c.id} value={c.id}>{c.company_name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Driver</label>
              <select
                value={formData.driver_id}
                onChange={(e) => setFormData({ ...formData, driver_id: e.target.value })}
              >
                <option value="">Select Driver</option>
                {drivers.map(d => (
                  <option key={d.id} value={d.id}>{d.first_name} {d.last_name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Truck</label>
            <select
              value={formData.truck_id}
              onChange={(e) => setFormData({ ...formData, truck_id: e.target.value })}
            >
              <option value="">Select Truck</option>
              {trucks.map(t => (
                <option key={t.id} value={t.id}>{t.truck_number}</option>
              ))}
            </select>
          </div>

          {selectedInventory.length > 0 ? (
            <div className="form-group" style={{ marginTop: 12 }}>
              <label>Load plan (stacking optimizer)</label>
              {!selectedTruck ? (
                <div style={{ color: '#666', fontSize: 13 }}>
                  Select a truck to run the loading optimizer.
                </div>
              ) : !selectedTruck.length_feet || !selectedTruck.width_feet || !selectedTruck.height_feet ? (
                <div style={{ color: '#b45309', fontSize: 13 }}>
                  This truck is missing dimensions. Add Length/Width/Height in Truck Management.
                </div>
              ) : !packResult ? (
                <div style={{ color: '#666', fontSize: 13 }}>
                  Add inventory pallets to see the load plan.
                </div>
              ) : (
                (() => {
                  const layers = packResult.layers || [];
                  const activeLayer = layers.find((l) => Number(l.index) === Number(activeLayerIndex)) || layers[0] || null;
                  const truckL = Number(packResult.truck?.length_feet || 0);
                  const truckW = Number(packResult.truck?.width_feet || 0);
                  const truckH = Number(packResult.truck?.height_feet || 0);
                  const usedH = Number(packResult.used?.height_feet || 0);

                  const svgW = isMobile ? 360 : 760;
                  const svgH = isMobile ? 300 : 360;
                  const pad = isMobile ? 10 : 12;
                  const labelFontSize = isMobile ? 8 : 11;

                  const placements = activeLayer?.placements || [];
                  const allPlacements = (packResult.layers || []).flatMap((ly) =>
                    (ly.placements || []).map((p) => ({ ...p, z0: Number(ly.z0 || 0) }))
                  );
                  const weights = allPlacements.map((p) => Number(p.weight_lbs || 0));
                  const minW = weights.length ? Math.min(...weights) : 0;
                  const maxW = weights.length ? Math.max(...weights) : 0;

                  const scale2d =
                    truckL > 0 && truckW > 0 ? Math.min((svgW - pad * 2) / truckL, (svgH - pad * 2) / truckW) : 1;

                  const scale3d =
                    truckL > 0 && truckW > 0
                      ? Math.min((svgW - 120) / (truckL + truckW), (svgH - 60) / ((truckL + truckW) * 0.5 + truckH))
                      : 1;
                  const originX = svgW * 0.52;
                  const originY = svgH * 0.72;

                  // 3D draw order (painter’s algorithm): draw far-to-near so nearer boxes sit on top.
                  // In this isometric projection, larger (x + y) tends to be closer; higher z should also be closer.
                  const sorted3d = [...allPlacements].sort((a, b) => {
                    const da = Number(a.x || 0) + Number(a.y || 0);
                    const db = Number(b.x || 0) + Number(b.y || 0);
                    if (da !== db) return da - db; // far (small) first
                    const za = Number(a.z0 || 0);
                    const zb = Number(b.z0 || 0);
                    return za - zb; // lower first, higher last
                  });

                  return (
                    <div className="freight-load-plan">
                      <div className="freight-load-viz-panel">
                        <div className="freight-load-toolbar">
                          <div className="freight-load-toolbar__status">
                            <span
                              className={`freight-fit-badge ${packResult.fits ? 'freight-fit-badge--ok' : 'freight-fit-badge--bad'}`}
                            >
                              {packResult.fits ? 'FITS' : 'DOES NOT FIT'}
                            </span>
                            <span className="freight-load-used">
                              Used (ft): L {Number(packResult.used.length_feet || 0).toFixed(2)} · W{' '}
                              {Number(packResult.used.width_feet || 0).toFixed(2)} · H{' '}
                              {Number(packResult.used.height_feet || 0).toFixed(2)}
                            </span>
                          </div>
                          {layers.length > 0 ? (
                            <div className="freight-load-toolbar__controls">
                              <span className="freight-load-toolbar__label">View</span>
                              <div className="freight-load-view-toggle">
                                <button
                                  type="button"
                                  className={loadViewMode === '3d' ? 'btn-primary' : 'btn-secondary'}
                                  onClick={() => setLoadViewMode('3d')}
                                >
                                  3D
                                </button>
                                <button
                                  type="button"
                                  className={loadViewMode === 'layer' ? 'btn-primary' : 'btn-secondary'}
                                  onClick={() => setLoadViewMode('layer')}
                                >
                                  Layer
                                </button>
                              </div>

                              {loadViewMode === 'layer' ? (
                                <div className="freight-load-layer-select">
                                  <span className="freight-load-toolbar__label">Layer</span>
                                  <select value={String(activeLayerIndex)} onChange={(e) => setActiveLayerIndex(Number(e.target.value))}>
                                    {layers.map((l) => (
                                      <option key={l.index} value={l.index}>
                                        {l.index} (z={Number(l.z0 || 0).toFixed(2)}ft, h={Number(l.height || 0).toFixed(2)}ft)
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              ) : (
                                <label className="freight-load-labels-check">
                                  <input type="checkbox" checked={show3dLabels} onChange={(e) => setShow3dLabels(e.target.checked)} />
                                  Labels
                                </label>
                              )}
                            </div>
                          ) : null}
                        </div>

                        {!packResult.fits && packResult.reasons?.length ? (
                          <div style={{ marginTop: 8, color: '#991b1b', fontSize: 12 }}>
                            {packResult.reasons.map((r, idx) => (
                              <div key={idx}>- {r}</div>
                            ))}
                          </div>
                        ) : null}

                        <div className="freight-load-svg-wrap">
                          {loadViewMode === 'layer' ? (
                            <>
                              <svg
                                className="freight-load-svg"
                                viewBox={`0 0 ${svgW} ${svgH}`}
                                preserveAspectRatio="xMidYMid meet"
                                role="img"
                                aria-label="Top-down layer load plan"
                              >
                                {/* Truck floor */}
                                <rect
                                  x={pad}
                                  y={pad}
                                  width={truckL * scale2d}
                                  height={truckW * scale2d}
                                  fill="white"
                                  stroke="#cbd5e1"
                                  strokeWidth="2"
                                />
                                {/* Placements */}
                                {placements.map((p, idx) => (
                                  <g key={idx}>
                                    <rect
                                      x={pad + p.x * scale2d}
                                      y={pad + p.y * scale2d}
                                      width={p.l * scale2d}
                                      height={p.w * scale2d}
                                      fill={weightColor(Number(p.weight_lbs || 0), minW, maxW)}
                                      fillOpacity="0.75"
                                      stroke="#334155"
                                      strokeOpacity="0.35"
                                    />
                                    <title>
                                      {`Item ${p.meta?.inventory_items_id ?? p.id}\n${Number(p.l).toFixed(2)}×${Number(p.w).toFixed(2)}×${Number(p.h).toFixed(2)} ft\n${Number(p.weight_lbs || 0).toFixed(1)} lb`}
                                    </title>
                                  </g>
                                ))}
                              </svg>
                              <p className="freight-load-caption">
                                Top-down view of layer {activeLayer?.index || 1}. Heavier pallets render “redder.”
                              </p>
                            </>
                          ) : (
                            <>
                              <svg
                                className="freight-load-svg"
                                viewBox={`0 0 ${svgW} ${svgH}`}
                                preserveAspectRatio="xMidYMid meet"
                                role="img"
                                aria-label="Isometric 3D load plan"
                              >
                                {/* Truck floor wireframe (3D) */}
                                {(() => {
                                  const p000 = isoProject(0, 0, 0, scale3d, originX, originY);
                                  const pL00 = isoProject(truckL, 0, 0, scale3d, originX, originY);
                                  const p0W0 = isoProject(0, truckW, 0, scale3d, originX, originY);
                                  const pLW0 = isoProject(truckL, truckW, 0, scale3d, originX, originY);
                                  const p00H = isoProject(0, 0, truckH, scale3d, originX, originY);
                                  const pL0H = isoProject(truckL, 0, truckH, scale3d, originX, originY);
                                  const p0WH = isoProject(0, truckW, truckH, scale3d, originX, originY);
                                  const pLWH = isoProject(truckL, truckW, truckH, scale3d, originX, originY);
                                  return (
                                    <g>
                                      <polyline points={poly([p000, pL00, pLW0, p0W0, p000])} fill="none" stroke="#cbd5e1" strokeWidth="2" />
                                      <polyline points={poly([p00H, pL0H, pLWH, p0WH, p00H])} fill="none" stroke="#e5e7eb" strokeWidth="1.5" />
                                      <line x1={p000.x} y1={p000.y} x2={p00H.x} y2={p00H.y} stroke="#e5e7eb" strokeWidth="1.5" />
                                      <line x1={pL00.x} y1={pL00.y} x2={pL0H.x} y2={pL0H.y} stroke="#e5e7eb" strokeWidth="1.5" />
                                      <line x1={p0W0.x} y1={p0W0.y} x2={p0WH.x} y2={p0WH.y} stroke="#e5e7eb" strokeWidth="1.5" />
                                      <line x1={pLW0.x} y1={pLW0.y} x2={pLWH.x} y2={pLWH.y} stroke="#e5e7eb" strokeWidth="1.5" />
                                    </g>
                                  );
                                })()}

                                {/* Boxes */}
                                {sorted3d.map((p, idx) => {
                                  const x0 = Number(p.x || 0);
                                  const y0 = Number(p.y || 0);
                                  const z0 = Number(p.z0 || 0);
                                  const l = Number(p.l || 0);
                                  const w = Number(p.w || 0);
                                  const h = Number(p.h || 0);

                                  const base = weightColor(Number(p.weight_lbs || 0), minW, maxW);
                                  const topC = shadeRgb(base, 0.25);
                                  const leftC = shadeRgb(base, -0.15);
                                  const rightC = shadeRgb(base, -0.28);

                                  const A = isoProject(x0, y0, z0, scale3d, originX, originY);
                                  const B = isoProject(x0 + l, y0, z0, scale3d, originX, originY);
                                  const C = isoProject(x0 + l, y0 + w, z0, scale3d, originX, originY);
                                  const D = isoProject(x0, y0 + w, z0, scale3d, originX, originY);

                                  const A2 = isoProject(x0, y0, z0 + h, scale3d, originX, originY);
                                  const B2 = isoProject(x0 + l, y0, z0 + h, scale3d, originX, originY);
                                  const C2 = isoProject(x0 + l, y0 + w, z0 + h, scale3d, originX, originY);
                                  const D2 = isoProject(x0, y0 + w, z0 + h, scale3d, originX, originY);

                                  const topPoly = poly([A2, B2, C2, D2]);
                                  const leftPoly = poly([D, A, A2, D2]);
                                  const rightPoly = poly([B, C, C2, B2]);

                                  const label = String(p.meta?.inventory_items_id ?? p.id);
                                  const labelPt = isoProject(x0 + l * 0.5, y0 + w * 0.5, z0 + h, scale3d, originX, originY);

                                  return (
                                    <g key={idx}>
                                      <polygon points={leftPoly} fill={leftC} stroke="#334155" strokeOpacity="0.18" />
                                      <polygon points={rightPoly} fill={rightC} stroke="#334155" strokeOpacity="0.18" />
                                      <polygon points={topPoly} fill={topC} stroke="#334155" strokeOpacity="0.22" />
                                      {show3dLabels ? (
                                        <text
                                          x={labelPt.x}
                                          y={labelPt.y}
                                          textAnchor="middle"
                                          dominantBaseline="central"
                                          fontSize={labelFontSize}
                                          fontWeight="700"
                                          fill="#0f172a"
                                          style={{ paintOrder: 'stroke', stroke: 'rgba(255,255,255,0.85)', strokeWidth: 3 }}
                                        >
                                          {label}
                                        </text>
                                      ) : null}
                                      <title>
                                        {`Item ${label}\n${l.toFixed(2)}×${w.toFixed(2)}×${h.toFixed(2)} ft\n${Number(p.weight_lbs || 0).toFixed(1)} lb\nLayer z0=${z0.toFixed(2)} ft`}
                                      </title>
                                    </g>
                                  );
                                })}
                              </svg>
                              <p className="freight-load-caption">
                                3D isometric view of all layers.{isMobile ? ' Pinch or scroll if needed.' : ' Labels show the inventory item id.'}
                              </p>
                            </>
                          )}
                        </div>
                      </div>

                      <div className="freight-load-side-panel">
                        <div className="freight-load-side-panel__title">Height stack</div>
                        <div className="freight-load-side-panel__meta">
                          Truck height: {truckH.toFixed(2)} ft
                          <br />
                          Used height: {usedH.toFixed(2)} ft
                        </div>
                        <div className="freight-height-bar">
                          <div
                            style={{
                              position: 'absolute',
                              left: 0,
                              right: 0,
                              bottom: 0,
                              height: `${truckH > 0 ? Math.min(100, (usedH / truckH) * 100) : 0}%`,
                              background: packResult.fits ? '#d1fae5' : '#fee2e2',
                              borderTop: `2px solid ${packResult.fits ? '#34d399' : '#fca5a5'}`,
                              borderRadius: '0 0 8px 8px',
                            }}
                          />
                          <div className="freight-height-bar__pct">
                            {truckH > 0 ? `${((usedH / truckH) * 100).toFixed(0)}%` : '—'}
                          </div>
                        </div>

                        <div className="freight-load-side-panel__meta">
                          Weight: {Number(packResult.weight?.total_tons || 0).toFixed(2)} tons
                          {packResult.weight?.capacity_tons ? (
                            <> / {Number(packResult.weight.capacity_tons).toFixed(2)} tons</>
                          ) : null}
                        </div>
                        {packResult.wastedPct != null ? (
                          <div className="freight-load-side-panel__meta">
                            Est. wasted volume: {Number(packResult.wastedPct).toFixed(1)}%
                          </div>
                        ) : null}
                      </div>
                    </div>
                  );
                })()
              )}
            </div>
          ) : null}

          <div className="form-row">
            <div className="form-group">
              <label>Pickup Date</label>
              <input
                type="date"
                value={formData.pickup_date}
                onChange={(e) => setFormData({ ...formData, pickup_date: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Delivery Date</label>
              <input
                type="date"
                value={formData.delivery_date}
                onChange={(e) => setFormData({ ...formData, delivery_date: e.target.value })}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Value ($)</label>
              <input
                type="number"
                step="0.01"
                value={formData.value}
                onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                readOnly={selectedInventory.length > 0}
              />
            </div>
            <div className="form-group">
              <label>Weight (tons)</label>
              <input
                type="number"
                step="0.01"
                value={formData.weight_tons}
                onChange={(e) => setFormData({ ...formData, weight_tons: e.target.value })}
                readOnly={selectedInventory.length > 0}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Length (feet)</label>
              <input
                type="number"
                step="0.01"
                value={formData.length_feet}
                onChange={(e) => setFormData({ ...formData, length_feet: e.target.value })}
                readOnly={selectedInventory.length > 0}
              />
            </div>
            <div className="form-group">
              <label>Width (feet)</label>
              <input
                type="number"
                step="0.01"
                value={formData.width_feet}
                onChange={(e) => setFormData({ ...formData, width_feet: e.target.value })}
                readOnly={selectedInventory.length > 0}
              />
            </div>
            <div className="form-group">
              <label>Height (feet)</label>
              <input
                type="number"
                step="0.01"
                value={formData.height_feet}
                onChange={(e) => setFormData({ ...formData, height_feet: e.target.value })}
                readOnly={selectedInventory.length > 0}
              />
            </div>
            <div className="form-group">
              <label>Quantity</label>
              <input
                type="number"
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                readOnly={selectedInventory.length > 0}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows="3"
            />
          </div>

          <div className="form-group">
            <label>Special Instructions</label>
            <textarea
              value={formData.special_instructions}
              onChange={(e) => setFormData({ ...formData, special_instructions: e.target.value })}
              rows="3"
            />
          </div>

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={() => { setShowModal(false); setEditingFreight(null); resetForm(); }}>
              Cancel
            </button>
            <button type="submit" className="btn-primary">Save</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default FreightManagement;


